import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { ArrowDown, UserRound, Users } from 'lucide-react'
import { chartPartOfWholeColor } from '@/config/chartTheme'
import Panel from '@/components/dashboards/Panel'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import { useQuery } from '@tanstack/react-query'
import {
  cleanFilters,
  compactCurrency,
  dashboardAnalyticsService,
  describeFilters,
  drillForIds,
  type AnalyticsFilters,
  type PeopleRole,
  type WorkloadResponse,
} from '@/services/dashboardAnalytics'
import { ANALYTICS_KEY } from '@/components/analytics/explorer/analyticsQueries'
import {
  openStageColumns,
  policyThresholds,
  sortWorkload,
  stageShort,
  workloadRows,
  type StageColumnDef,
  type WorkloadRow,
  type WorkloadSort,
} from './workloadModel'

export function personHref(name: string, role: PeopleRole = 'responsibleParty'): string {
  return `/dashboards/people?view=person&role=${encodeURIComponent(role)}&name=${encodeURIComponent(name)}`
}

export function useWorkload(cohort: AnalyticsFilters, role: PeopleRole = 'responsibleParty', enabled = true) {
  const filters = cleanFilters(cohort)
  return useQuery({
    enabled,
    queryKey: [...ANALYTICS_KEY, 'workload', role, JSON.stringify(filters)],
    queryFn: ({ signal }) => dashboardAnalyticsService.workload({ role, filters }, signal),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

function Count({
  ids,
  count,
  title,
  tone = 'neutral',
  amount,
}: {
  ids: string[] | undefined
  count: number | null | undefined
  title: string
  tone?: 'neutral' | 'warning' | 'negative'
  amount?: number | null
}) {
  const { open } = useDrillDown()
  if (count == null) return <span className="text-stone-600" title="Not measured for this role">--</span>
  if (count === 0) return <span className="text-stone-700">·</span>
  return (
    <button
      type="button"
      onClick={() => open(drillForIds(ids, title))}
      title={`${title} — open ${count} deal${count === 1 ? '' : 's'}`}
      className={clsx(
        'inline-flex flex-col items-end leading-tight underline decoration-dotted decoration-amber-400/25 underline-offset-[3px] hover:text-amber-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50',
        tone === 'negative' ? 'text-red-300' : tone === 'warning' ? 'text-amber-300' : 'text-stone-100',
      )}
    >
      <span>{count.toLocaleString('en-US')}</span>
      {amount !== undefined && <span className="text-[9px] text-stone-500">{compactCurrency(amount)}</span>}
    </button>
  )
}

/** The person's open deals as one bar, a segment per stage in pipeline
 *  order, scaled to the busiest person — the bird's-eye view (T-56). */
function StageBar({ row, stages, max }: { row: WorkloadRow; stages: StageColumnDef[]; max: number }) {
  const { open } = useDrillDown()
  return (
    <div className="flex h-2.5 w-[120px] min-w-[72px] overflow-hidden rounded-sm bg-white/[0.03]">
      <div className="flex h-full" style={{ width: `${(row.open.count / max) * 100}%` }}>
        {stages.map((stage, index) => {
          const cell = row.byStage.get(stage.stage)
          if (!cell || cell.count === 0) return null
          return (
            <button
              key={stage.stage}
              type="button"
              tabIndex={-1}
              onClick={() => open(drillForIds(cell.dealIds, `${row.name} — ${stage.label}`))}
              className="h-full transition-[filter] hover:brightness-125"
              style={{
                width: `${(cell.count / Math.max(1, row.open.count)) * 100}%`,
                background: chartPartOfWholeColor(index, stages.length),
              }}
              title={`${stage.label}: ${cell.count}`}
            />
          )
        })}
      </div>
    </div>
  )
}

interface TeamWorkloadTableProps {
  data: WorkloadResponse
  compact?: boolean
  className?: string
}

/**
 * The team at a glance: for each person, the open deals they are responsible
 * for split by stage, the deals they support, staffed vs needing staffing, and
 * what has aged past the desk's thresholds. Every number opens exactly its
 * deals — the drill is where a manager reassigns (T-56/T-58/T-73/T-74).
 */
export function TeamWorkloadTable({ data, compact = false, className }: TeamWorkloadTableProps) {
  const rows = useMemo(() => workloadRows(data), [data])
  const stages = useMemo(() => openStageColumns(data), [data])
  const [sort, setSort] = useState<WorkloadSort>('open')
  const sorted = useMemo(() => sortWorkload(rows, sort), [rows, sort])
  const max = Math.max(1, ...rows.map((row) => row.open.count))
  const policy = policyThresholds(data.policy)
  const hasFunded = rows.some((row) => (row.funded?.count ?? 0) > 0)
  const staffingKnown = rows.some((row) => row.needsStaffing != null)

  const header = (key: WorkloadSort, label: string, hint: string, className?: string) => (
    <th scope="col" className={clsx('!px-1.5 text-right', className)} title={hint} aria-sort={sort === key ? 'descending' : 'none'}>
      <button
        type="button"
        onClick={() => setSort(key)}
        className={clsx('inline-flex items-center gap-0.5 uppercase hover:text-stone-200', sort === key && 'text-amber-200')}
      >
        {label}
        {sort === key && <ArrowDown size={8} />}
      </button>
    </th>
  )

  return (
    <div className={clsx('min-h-0 overflow-auto scrollbar-thin', className)}>
      <table className="dense-table">
        <thead className="sticky top-0 z-[1]">
          <tr>
            <th scope="col" className="!px-2">Person</th>
            {header('open', 'Open', 'Open deals they are Responsible Party on (not funded, not dead) · total commitment')}
            <th scope="col" className="!px-1.5">{compact ? 'Mix' : 'Open by stage'}</th>
            {!compact &&
              stages.map((stage) => (
                <th key={stage.stage} scope="col" className="!px-1.5 text-right" title={stage.label}>
                  {stageShort(stage.label)}
                </th>
              ))}
            {hasFunded && !compact && header('funded', 'Funded', 'Funded deals they are Responsible Party on')}
            {!compact && header('supporting', 'Support', 'Deals naming them as Underwriter, Secondary, Analyst or Originator where someone else is Responsible Party (T-55)')}
            {staffingKnown && header('needsStaffing', 'To staff', 'Open deals with no Analyst and no Secondary Underwriter (T-58)')}
            {header('triageAged', `Triage ${policy.triage ?? '?'}d+`, `In Triage ${policy.triage ?? '?'}+ whole days, neither quoted nor killed (T-73)`)}
            {header('quotesAged', `Quotes >${policy.quote ?? '?'}d`, `Quotes out more than ${policy.quote ?? '?'} days with no term sheet and no kill (T-74)`)}
            {header('quoteSla', 'Quote SLA', `Not quoted ${policy.quoteSla ?? '?'}+ days after receipt (T-72; 24-hour days, weekends count)`)}
            <th scope="col" className="!px-1.5" aria-label="Profile" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.name}>
              <td className="!px-2">
                <span
                  className={clsx('block max-w-[170px] truncate text-[11.5px]', row.unassigned ? 'italic text-stone-500' : 'text-stone-100')}
                  title={row.onRoster ? `${row.name} · on the team roster` : row.name}
                >
                  {row.name}
                </span>
              </td>
              <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                <Count ids={row.open.ids} count={row.open.count} title={`${row.name} — open deals`} amount={compact ? undefined : row.open.volume} />
              </td>
              <td className="!px-1.5">
                <StageBar row={row} stages={stages} max={max} />
              </td>
              {!compact &&
                stages.map((stage) => {
                  const cell = row.byStage.get(stage.stage)
                  return (
                    <td key={stage.stage} className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                      <Count ids={cell?.dealIds} count={cell?.count ?? 0} title={`${row.name} — ${stage.label}`} />
                    </td>
                  )
                })}
              {hasFunded && !compact && (
                <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                  <Count ids={row.funded?.dealIds} count={row.funded?.count ?? 0} title={`${row.name} — funded`} />
                </td>
              )}
              {!compact && (
              <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                <Count ids={row.supporting.ids} count={row.unassigned ? null : row.supporting.count} title={`${row.name} — supporting`} />
              </td>
              )}
              {staffingKnown && (
                <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                  <Count ids={row.needsStaffing?.dealIds} count={row.needsStaffing?.count ?? null} title={`${row.name} — needs staffing`} tone="warning" />
                </td>
              )}
              <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                <Count ids={row.aging?.triageAgedIds} count={row.aging?.triageAgedCount ?? null} title={`${row.name} — aged in triage`} tone="warning" />
              </td>
              <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                <Count ids={row.aging?.quotesOutAgedIds} count={row.aging?.quotesOutAgedCount ?? null} title={`${row.name} — aged quotes`} tone="warning" />
              </td>
              <td className="!px-1.5 text-right font-mono text-[11px] tabular-nums">
                <Count ids={row.aging?.quoteSlaBreachIds} count={row.aging?.quoteSlaBreachCount ?? null} title={`${row.name} — quote SLA breached`} tone="negative" />
              </td>
              <td className="!px-1.5 text-right">
                {!row.unassigned && (
                  <Link
                    to={personHref(row.name, 'responsibleParty')}
                    aria-label={`Open ${row.name}'s profile`}
                    className="inline-flex items-center rounded border border-amber-400/15 p-1 text-amber-200/80 hover:bg-amber-400/10"
                  >
                    <UserRound size={10} />
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** One-line statement of staffing coverage and the policy behind the aging
 *  columns — printed under every workload table (T-57). */
export function workloadFootnote(data: WorkloadResponse | undefined): string {
  if (!data) return ''
  const policy = policyThresholds(data.policy)
  const parts = [
    data.staffing?.analystCoverage ? `Staffing: ${data.staffing.analystCoverage}` : null,
    data.staffing?.secondaryUnderwriterCoverage ?? null,
    data.staffing?.analystCoverageN === 0 && data.staffing?.secondaryUnderwriterCoverageN === 0
      ? 'the deal book carries neither, so every open deal reads as needing staffing'
      : null,
  ].filter(Boolean)
  const thresholds = `Aging thresholds: triage ${policy.triage ?? '?'}d, quotes ${policy.quote ?? '?'}d, quote SLA ${policy.quoteSla ?? '?'}d${policy.ratified ? '' : ' (pending sign-off)'}; no hand-entered last contact is read (T-74).`
  return `${parts.join(' · ')}${parts.length ? '. ' : ''}${thresholds}`
}

interface TeamWorkloadPanelProps {
  cohort?: AnalyticsFilters
  compact?: boolean
  title?: string
  className?: string
  actions?: React.ReactNode
  /** The document the host already holds (a board's `workload` panel) —
   *  skips the request. */
  served?: WorkloadResponse | null
  /** Host loading state while `served` is on its way. */
  servedLoading?: boolean
}

/** Panel wrapper: loading / error / empty handled by the kit. */
export default function TeamWorkloadPanel({
  cohort = {},
  compact = false,
  title = 'Team workload',
  className,
  actions,
  served,
  servedLoading = false,
}: TeamWorkloadPanelProps) {
  const hosted = served !== undefined
  const query = useWorkload(cohort, 'responsibleParty', !hosted)
  const data = hosted ? served ?? undefined : query.data
  return (
    <Panel
      title={title}
      kicker={<span title={data?.basis}>{describeFilters(cohort)}</span>}
      actions={actions}
      isLoading={hosted ? servedLoading : query.isLoading}
      error={hosted ? null : query.error}
      onRetry={hosted ? undefined : () => void query.refetch()}
      isEmpty={!(hosted ? servedLoading : query.isLoading) && (data?.people.length ?? 0) === 0}
      emptyIcon={Users}
      emptyTitle="No one on record"
      emptyDescription="No live deal names a responsible party and the team roster is empty."
      className={className}
      bodyClassName="!p-0 flex min-h-0 flex-col"
      footer={
        data ? (
          <BasisNote full={workloadFootnote(data)}>
            {data.staffing?.analystCoverage
              ? `Staffing: ${data.staffing.analystCoverage}${data.staffing.analystCoverageN === 0 ? ', so every open deal needs staffing' : ''}. Aging thresholds await sign-off.`
              : 'Aging thresholds await sign-off.'}
          </BasisNote>
        ) : undefined
      }
    >
      {data && <TeamWorkloadTable data={data} compact={compact} className="flex-1" />}
    </Panel>
  )
}
